import React, { useState } from 'react';
import _ from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRootDispatch, IRootState } from 'stores/rematch/root-store';
import { createContext, useContextSelector } from 'use-context-selector';
import { BookingView } from 'stores/rematch/models/types/bookings-store';

const noop = () => {};

type BookingViewTabContextType = {
  dirtyIds: string[];
  revertViewSnapshot: (customId: string) => void;
  revertSnapshot: () => void;
  updateViewSnapshot: (view: BookingView) => void;
  updateSnapshot: (opts?: { preserve?: boolean }) => void;
  removeViewFromSnapshot: (customId: string) => void;
  registerOnRevertEvent: (event: RevertEvent) => void;
  unregisterOnRevertEvent: (event: RevertEvent) => void;
  clearOnRevertEvents: () => void;
};

const BookingTabViewContext = createContext<BookingViewTabContextType>({
  dirtyIds: [],
  revertViewSnapshot: noop,
  revertSnapshot: noop,
  updateViewSnapshot: noop,
  updateSnapshot: noop,
  removeViewFromSnapshot: noop,
  registerOnRevertEvent: noop,
  unregisterOnRevertEvent: noop,
  clearOnRevertEvents: noop,
});

interface BookingTabViewProviderProps {
  children: React.ReactNode;
}

export type RevertEvent = (tabToRevert: BookingView) => Promise<void> | void;

export const BookingTabViewProvider = ({ children }: BookingTabViewProviderProps) => {
  const { bookingsStore } = useDispatch<IRootDispatch>();
  const defaultBookingViews = useSelector((state: IRootState) => state.bookingsStore.defaultBookingViews);
  const displayedBookingListingTabs = useSelector(
    (state: IRootState) => state.bookingsStore.displayedBookingListingTabs,
  );
  const setBookingsFilterV2 = bookingsStore.setBookingsFilterV2;
  const setHasBookingListingFilterChanged = bookingsStore.setHasBookingListingFilterChanged;
  const snapshot = useRef(_.cloneDeep([...defaultBookingViews, ...displayedBookingListingTabs]));
  const [onRevertEvents, setOnRevertEvents] = useState<RevertEvent[]>([]);

  const registerOnRevertEvent = useCallback((event: RevertEvent) => {
    setOnRevertEvents((prev) => [...prev, event]);
  }, []);

  const triggerOnRevertEvents = useCallback(
    async (tabToRevert: BookingView) => {
      for (const event of onRevertEvents) {
        await event(tabToRevert);
      }
    },
    [onRevertEvents],
  );

  const clearOnRevertEvents = useCallback(() => {
    setOnRevertEvents([]);
  }, []);

  const unregisterOnRevertEvent = useCallback((event: RevertEvent) => {
    setOnRevertEvents((prev) => prev.filter((e) => e !== event));
  }, []);

  const revertViewSnapshot = useCallback(
    async (customId: string) => {
      const tabToRevert = snapshot.current.find((tab) => tab.customViewId === customId);
      if (tabToRevert) {
        setHasBookingListingFilterChanged(true);
        setBookingsFilterV2(tabToRevert.filterValue);
        await triggerOnRevertEvents(tabToRevert);
      }
    },
    [setBookingsFilterV2, setHasBookingListingFilterChanged, triggerOnRevertEvents],
  );

  const revertSnapshot = useCallback(() => {
    // biome-ignore lint/complexity/noForEach: yay for forEach
    snapshot.current.forEach((tab) => {
      setBookingsFilterV2(tab.filterValue);
    });
  }, [setBookingsFilterV2]);

  const updateViewSnapshot = useCallback((next: BookingView) => {
    const customViewId = next.customViewId;
    const currentTab = snapshot.current.find((tab) => tab.customViewId === customViewId);

    if (!currentTab) {
      snapshot.current.push(_.cloneDeep(next));
      return;
    }
    snapshot.current = snapshot.current.map((tab) => {
      if (tab.customViewId === customViewId) {
        return _.cloneDeep(next);
      }
      return tab;
    });
  }, []);

  const removeViewFromSnapshot = useCallback((customId: string) => {
    snapshot.current = snapshot.current.filter((tab) => tab.customViewId !== customId);
  }, []);

  const updateSnapshot = useCallback(
    (opts?: {
      preserve?: boolean;
    }) => {
      if (opts?.preserve) {
        const currentState = [...defaultBookingViews, ...displayedBookingListingTabs];
        const newSnapshot = currentState.map((tab) => {
          const existingTab = snapshot.current.find((t) => t.customViewId === tab.customViewId);
          if (existingTab) {
            return existingTab;
          }
          return _.cloneDeep(tab);
        });
        snapshot.current = newSnapshot;
        return;
      }
      snapshot.current = _.cloneDeep([...defaultBookingViews, ...displayedBookingListingTabs]);
    },
    [defaultBookingViews, displayedBookingListingTabs],
  );

  const dirtyIds = useMemo(() => {
    const dirtyIds: string[] = [];
    const currentTabs = [...defaultBookingViews, ...displayedBookingListingTabs];
    // biome-ignore lint/complexity/noForEach: yay for forEach
    snapshot.current.forEach((tab) => {
      const currentTab = currentTabs.find((t) => t.customViewId === tab.customViewId);
      if (!_.isEqual(currentTab?.filterValue, tab.filterValue)) {
        if (currentTab?.customViewId) {
          dirtyIds.push(currentTab.customViewId);
        }
      }
    });
    return dirtyIds;
  }, [defaultBookingViews, displayedBookingListingTabs]);

  const value = useMemo(
    () => ({
      dirtyIds,
      revertViewSnapshot,
      revertSnapshot,
      updateViewSnapshot,
      updateSnapshot,
      removeViewFromSnapshot,
      registerOnRevertEvent,
      unregisterOnRevertEvent,
      clearOnRevertEvents,
    }),
    [
      dirtyIds,
      revertViewSnapshot,
      revertSnapshot,
      updateViewSnapshot,
      updateSnapshot,
      removeViewFromSnapshot,
      registerOnRevertEvent,
      unregisterOnRevertEvent,
      clearOnRevertEvents,
    ],
  );

  return <BookingTabViewContext.Provider value={value}>{children}</BookingTabViewContext.Provider>;
};

type Selector<T, Context> = (value: Context) => T;

export const useBookingTabs = <T,>(selector: Selector<T, BookingViewTabContextType>) =>
  useContextSelector(BookingTabViewContext, selector);
